import {
  forwardRef,
  useEffect,
  useId,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

/** Class-name join. No dependency needed for something this small. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'record';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 font-medium rounded ' +
  'transition-colors duration-120 select-none whitespace-nowrap ' +
  'disabled:opacity-40 disabled:pointer-events-none';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-chalk text-slate-950 hover:bg-white active:bg-chalk-dim',
  secondary: 'bg-slate-800 text-chalk border border-hairline hover:bg-slate-700 hover:border-slate-600',
  ghost: 'text-chalk-dim hover:text-chalk hover:bg-slate-800',
  danger: 'bg-transparent text-danger border border-danger/40 hover:bg-danger/10',
  // The only place `record` red is allowed on a control.
  record: 'bg-record text-white hover:bg-[#ff5147] active:bg-record-dim',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-caption',
  md: 'h-9 px-3.5 text-body',
  lg: 'h-11 px-5 text-prose',
};

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', busy = false, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || busy}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...rest}
    >
      {busy ? <Spinner size={size === 'lg' ? 18 : 14} /> : null}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={cx('animate-spin', className)}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2.5" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2.5 text-chalk-faint text-body py-10 justify-center">
      <Spinner />
      <span>{label}…</span>
    </div>
  );
}

export type Tone = 'info' | 'caution' | 'danger' | 'go';

const TONE_STYLES: Record<Tone, string> = {
  info: 'border-signal/30 bg-signal/[0.07] text-chalk',
  caution: 'border-caution/35 bg-caution/[0.07] text-chalk',
  danger: 'border-danger/35 bg-danger/[0.07] text-chalk',
  go: 'border-go/30 bg-go/[0.07] text-chalk',
};

const TONE_DOT: Record<Tone, string> = {
  info: 'bg-signal',
  caution: 'bg-caution',
  danger: 'bg-danger',
  go: 'bg-go',
};

export function Banner({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      className={cx(
        'flex flex-col gap-2 rounded-md border px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        TONE_STYLES[tone],
      )}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <div className="flex gap-3">
        <span className={cx('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT[tone])} />
        <div className="space-y-0.5">
          {title ? <p className="font-medium text-body">{title}</p> : null}
          {children ? <div className="text-caption text-chalk-dim">{children}</div> : null}
        </div>
      </div>
      {action ? <div className="shrink-0 sm:pl-4">{action}</div> : null}
    </div>
  );
}

/** Inline form/field error. Small, red, never a modal. */
export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p className="text-caption text-danger flex items-start gap-1.5" role="alert">
      <span aria-hidden="true" className="mt-[0.4em] h-1 w-1 shrink-0 rounded-full bg-danger" />
      <span>{children}</span>
    </p>
  );
}

export function EmptyState({
  title,
  children,
  action,
  icon,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-hairline px-6 py-14 text-center">
      {icon ? <div className="mb-4 text-chalk-faint">{icon}</div> : null}
      <h3 className="text-h4 text-chalk">{title}</h3>
      {children ? <div className="mt-2 max-w-md text-body text-chalk-dim">{children}</div> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title = 'That did not work',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="rounded-md border border-danger/30 bg-danger/[0.06] px-5 py-6">
      <h3 className="text-h4">{title}</h3>
      <p className="mt-1.5 text-body text-chalk-dim">{message}</p>
      {onRetry ? (
        <Button className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------

const FIELD_BASE =
  'w-full rounded bg-slate-900 border border-hairline px-3 text-chalk placeholder:text-chalk-faint ' +
  'transition-colors duration-120 hover:border-slate-600 focus:border-signal focus:outline-none ' +
  'disabled:opacity-50';

export interface InputProps extends ComponentPropsWithoutRef<'input'> {
  label?: string;
  hint?: string;
  error?: string | null;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, className, id, ...rest },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={fieldId} className="block text-caption font-medium text-chalk-dim">
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={fieldId}
        className={cx(FIELD_BASE, 'h-9 text-body', error && 'border-danger/60', className)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
      {hint && !error ? <p className="text-caption text-chalk-faint">{hint}</p> : null}
      <ErrorText>{error}</ErrorText>
    </div>
  );
});

export interface TextareaProps extends ComponentPropsWithoutRef<'textarea'> {
  label?: string;
  error?: string | null;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, className, id, ...rest },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={fieldId} className="block text-caption font-medium text-chalk-dim">
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={fieldId}
        className={cx(FIELD_BASE, 'py-2 text-body resize-y', error && 'border-danger/60', className)}
        {...rest}
      />
      <ErrorText>{error}</ErrorText>
    </div>
  );
});

export interface SelectProps extends ComponentPropsWithoutRef<'select'> {
  label?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, className, id, children, ...rest },
  ref,
) {
  const generated = useId();
  const fieldId = id ?? generated;
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={fieldId} className="block text-caption font-medium text-chalk-dim">
          {label}
        </label>
      ) : null}
      <select
        ref={ref}
        id={fieldId}
        className={cx(FIELD_BASE, 'h-9 text-body pr-8 appearance-none', className)}
        {...rest}
      >
        {children}
      </select>
    </div>
  );
});

/**
 * A labelled on/off control for the recorder's source toggles.
 *
 * Three states, not two. `pending` is "you have asked for this and it is not
 * running yet", which is what a microphone looks like between a remembered
 * preference and the permission that has not been granted. It is drawn with a
 * dashed edge rather than another color, and `hint` puts the state in words
 * inside the button, so it reaches a screen reader as part of the button's
 * name rather than only reaching people who can see the border.
 */
export function Toggle({
  active,
  onChange,
  icon,
  label,
  hint,
  pending = false,
  disabled,
  title,
}: {
  active: boolean;
  onChange: () => void;
  icon: ReactNode;
  label: string;
  hint?: string;
  pending?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      title={title ?? label}
      aria-pressed={active}
      className={cx(
        'flex items-center gap-2 rounded border px-3 h-9 text-body transition-colors duration-120',
        'disabled:opacity-40 disabled:pointer-events-none',
        active
          ? 'border-chalk/25 bg-slate-700 text-chalk'
          : pending
            ? 'border-dashed border-chalk/30 bg-slate-850 text-chalk-dim hover:text-chalk hover:border-chalk/50'
            : 'border-hairline bg-slate-900 text-chalk-faint hover:text-chalk-dim hover:border-slate-600',
      )}
    >
      <span aria-hidden="true" className={active ? 'text-chalk' : 'text-chalk-faint'}>
        {icon}
      </span>
      <span>{label}</span>
      {hint ? <span className="text-caption text-chalk-faint">{hint}</span> : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export function Badge({
  tone = 'info',
  children,
  pulse = false,
}: {
  tone?: Tone | 'neutral' | 'record';
  children: ReactNode;
  pulse?: boolean;
}) {
  const styles: Record<string, string> = {
    neutral: 'border-hairline bg-slate-800 text-chalk-dim',
    info: 'border-signal/30 bg-signal/10 text-signal',
    caution: 'border-caution/30 bg-caution/10 text-caution',
    danger: 'border-danger/30 bg-danger/10 text-danger',
    go: 'border-go/30 bg-go/10 text-go',
    record: 'border-record/40 bg-record/10 text-record',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-xs border px-1.5 py-0.5 text-micro uppercase tracking-[0.08em]',
        styles[tone] ?? styles.neutral,
      )}
    >
      {pulse ? (
        <span className="h-1.5 w-1.5 rounded-full bg-current animate-record-pulse" aria-hidden="true" />
      ) : null}
      {children}
    </span>
  );
}

/** A keyboard hint. Used liberally in the recorder, where speed matters. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-xs border border-hairline bg-slate-900 px-1.5 py-0.5 font-mono text-micro text-chalk-faint">
      {children}
    </kbd>
  );
}

export function CopyButton({
  value,
  label = 'Copy link',
  copiedLabel = 'Copied',
  variant = 'secondary',
  size = 'md',
  className,
  onCopied,
}: {
  value: string;
  label?: string;
  copiedLabel?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  /** Ran once the link is actually on the clipboard. */
  onCopied?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    setFailed(false);
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopied?.();
    } catch {
      // Clipboard access is denied in some embedded contexts; select-to-copy
      // is the honest fallback rather than pretending it worked.
      setFailed(true);
    }
  };

  return (
    <div className={cx('inline-flex flex-col items-start gap-1', className)}>
      <Button variant={variant} size={size} onClick={() => void copy()}>
        {copied ? copiedLabel : label}
      </Button>
      {failed ? (
        <span className="text-micro text-chalk-faint">
          Your browser blocked the clipboard. Select the link and copy it.
        </span>
      ) : null}
    </div>
  );
}

/** Page-level heading with the clapper rule underneath it. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-h2 text-chalk">{title}</h1>
          {subtitle ? <p className="mt-1 text-body text-chalk-dim">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      <div className="clapper-stripes mt-4 h-[3px] w-full opacity-[0.12]" aria-hidden="true" />
    </header>
  );
}
